import { Box } from "@vinomofo/components";
import { primaryCTAType, StorefrontImage } from "../../../types";
import { PropsWithChildren, useCallback } from "react";
import { Button } from "../../button";
import { GatsbyImage } from "gatsby-plugin-image";
import styled from "styled-components";
import { trackEvent } from "../../../utils/track-event";
import { useLocation } from "@reach/router";
import { generateStorefrontImage } from "../../../utils/generate-storefront-image";
import { BannerHeroTimer } from "../../banner-hero-timer/BannerHeroTimer";

export interface AdBlockProps {
  backgroundImage: StorefrontImage;
  backgroundImageSmall?: StorefrontImage;
  primaryCta: primaryCTAType;
  buttonPosition: string;
  eventPage?: boolean;
  countdownTimer?: string;
  shortText?: string;
  longText?: string;
  imageURLs?: {
    desktop: string;
    mobile: string;
  };
  hubspotFormId?: string;
  layout?: string;
  pageSectionIndex?: number;
}

const StyledSmallImage = styled(Box)`
  display: none;

  @media (max-width: 500px) {
    display: block;
    margin: 0 -20px;
  }
`;

const StyledLargeImage = styled(Box)`
  display: none;

  @media (min-width: 501px) {
    display: block;
  }
`;

const AdBlockContainer = styled(Box)`
  img {
    border-radius: 10px;

    @media (max-width: 500px) {
      border-radius: 0;
    }
  }
`;

export const adBlock = (data) => {
  const transformedData = {
    backgroundImage: generateStorefrontImage(data.image),
    backgroundImageSmall: generateStorefrontImage(data.backgroundImageSmall),
    primaryCta: data.primaryCta,
    buttonPosition: data.buttonPosition,
    countdownTimer: data.countdownTimer,
    shortText: data.shortText,
    longText: data.longText,
    eventPage: true,
    hubspotFormId: data.hubspotFormId,
    layout: data.layout,
  };

  return <AdBlock {...transformedData} />;
};

export const AdBlock = ({
  backgroundImage,
  backgroundImageSmall,
  primaryCta,
  buttonPosition,
  eventPage = false,
  countdownTimer,
  shortText,
  longText,
  imageURLs,
  hubspotFormId,
  layout,
  pageSectionIndex,
}: PropsWithChildren<AdBlockProps>): JSX.Element => {
  const location = useLocation();
  const ctaLink =
    primaryCta?.linkTo?.path ||
    primaryCta?.linkTo?.url ||
    primaryCta?.linkTo?.slug;
  const ctaTarget = primaryCta?.linkTo?.target === "New Tab" ? "_new" : "_self";

  const signupAdBlock = hubspotFormId || countdownTimer || longText;

  const positionStyles = {
    left: { bottom: "24px", left: "54px", textAlign: "left" },
    center: {
      bottom: "24px",
      left: "0",
      right: "0",
      margin: "auto",
      transform: "translateX(-50%)",
      textAlign: "center",
    },
    right: { bottom: "24px", right: "54px", textAlign: "right" },
  };

  const positionMobileStyles = {
    left: { bottom: "24px", left: "14px", textAlign: "left" },
    center: {
      bottom: "24px",
      left: "0",
      right: "0",
      margin: "auto",
      transform: "translateX(-50%)",
      textAlign: "center",
    },
    right: { bottom: "24px", right: "14px", textAlign: "right" },
  };

  const handleTrackingEvent = useCallback(async () => {
    trackEvent("Ad Block Clicked", {
      location: location?.pathname,
    });
  }, [location]);

  return (
    <>
      {signupAdBlock ? (
        <BannerHeroTimer
          backgroundImage={imageURLs?.desktop || ""}
          mobileBackgroundImage={imageURLs?.mobile || ""}
          subheading={shortText || ""}
          formId={hubspotFormId}
          countdownTimer={countdownTimer}
          heading={longText}
          primaryCta={primaryCta}
          pageSectionIndex={pageSectionIndex}
        />
      ) : (
        <AdBlockContainer
          display="flex" 
          flexDirection="column" 
          position="relative" 
          alignItems="center"
          mt={eventPage ? "-20px" : undefined}
          gridColumn={eventPage ? !layout || layout === "Fullwidth" ? "1 / -1" : `span ${2}` : undefined}
        >
          <a
            href={ctaLink}
            target={ctaTarget}
            rel="noreferrer"
            style={{
              border: 0,
              textDecoration: "none",
              background: 0,
              width: "100%",
            }}
            onClick={() => {
              trackEvent("Ad Block Clicked", {
                location: location?.pathname,
              });
            }}
          >
            <StyledLargeImage position="relative" height="100%">
              <Box borderRadius={["4px 4px 0 0", "8px"]} overflow="hidden">
                {backgroundImage && (
                  <GatsbyImage
                    image={backgroundImage}
                    objectFit="cover"
                    objectPosition="center center"
                    style={{
                      width: "100%",
                      height: "100%",
                    }}
                    alt=""
                  />
                )}
              </Box>

              {ctaLink && (
                <Box
                  position="absolute"
                  {...positionStyles[
                    buttonPosition?.toLocaleLowerCase() || "left"
                  ]}
                  textAlign="center"
                >
                  <Button
                    to={ctaLink}
                    target={ctaTarget}
                    as={Box}
                    style={{
                      padding: "10px 34px",
                      fontSize: "14px",
                      textDecoration: "none",
                    }}
                    color={primaryCta.buttonTextColor || "white"}
                    backgroundColor={primaryCta.buttonColour}
                    borderColor={primaryCta.buttonBorderColor}
                    hoverBorderColor={primaryCta.buttonHoverBorderColor}
                    hoverBackgroundColor={primaryCta.buttonHoverBackgroundColor}
                    hoverTextColor={primaryCta.buttonHoverTextColor}
                    onClick={() => handleTrackingEvent}
                    isCTA
                  >
                    {primaryCta.label}
                  </Button>
                </Box>
              )}
            </StyledLargeImage>

            <StyledSmallImage overflow="hidden">
              {backgroundImageSmall && (
                <GatsbyImage
                  image={backgroundImageSmall}
                  objectFit="cover"
                  objectPosition="center center"
                  style={{
                    width: "100%",
                    height: "100%",
                  }}
                  alt=""
                />
              )}
              {ctaLink && (
                <Box
                  position="absolute"
                  {...positionMobileStyles[
                    buttonPosition?.toLocaleLowerCase() || "left"
                  ]}
                  textAlign="center"
                >
                  <Button
                    to={ctaLink}
                    target={ctaTarget}
                    as={Box}
                    style={{
                      padding: "10px 34px",
                      fontSize: "14px",
                      textDecoration: "none",
                    }}
                    color={primaryCta.buttonTextColor || "white"}
                    backgroundColor={primaryCta.buttonColour}
                    borderColor={primaryCta.buttonBorderColor}
                    hoverBorderColor={primaryCta.buttonHoverBorderColor}
                    hoverBackgroundColor={primaryCta.buttonHoverBackgroundColor}
                    hoverTextColor={primaryCta.buttonHoverTextColor}
                    onClick={() => handleTrackingEvent}
                    isCTA
                  >
                    {primaryCta.label}
                  </Button>
                </Box>
              )}
            </StyledSmallImage>
          </a>
        </AdBlockContainer>
      )}
    </>
  );
};
